import { useCallback, useRef, useState } from "react";
import type { Ear } from "../type/tonalTest";
import { dbToVolume } from "./audioLevels";

// Same pulsed-tone pattern as the tonal audiometry test panel.
const PULSE_ON = 0.3;
const PULSE_OFF = 0.3;

type ToneParams = { ear: Ear; freq: number; db: number };

// Shared pulsed-tone player so every caller (both ear charts) emits sound the same way.
export function useIntermittentTone() {
  const audioCtxRef = useRef<AudioContext | null>(null);
  const intervalRef = useRef<number | null>(null);
  const offTimeoutRef = useRef<number | null>(null);
  const paramsRef = useRef<ToneParams | null>(null);
  const [isOn, setIsOn] = useState(false);

  const getContext = useCallback(() => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new AudioContext();
    }
    return audioCtxRef.current;
  }, []);

  const playPulse = useCallback(() => {
    const params = paramsRef.current;
    if (!params) return;

    const ctx = getContext();
    const panner = new StereoPannerNode(ctx, { pan: params.ear === "left" ? -1 : 1 });
    panner.connect(ctx.destination);

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.frequency.value = params.freq;
    osc.type = "sine";
    gain.gain.value = dbToVolume(params.db);
    osc.connect(gain).connect(panner);
    osc.start();
    osc.stop(ctx.currentTime + PULSE_ON);

    // Keep the on-screen cursor blinking in sync with the audible pulse.
    setIsOn(true);
    if (offTimeoutRef.current !== null) window.clearTimeout(offTimeoutRef.current);
    offTimeoutRef.current = window.setTimeout(() => setIsOn(false), PULSE_ON * 1000);
  }, [getContext]);

  const update = useCallback((ear: Ear, freq: number, db: number) => {
    paramsRef.current = { ear, freq, db };
  }, []);

  const start = useCallback((ear: Ear, freq: number, db: number) => {
    update(ear, freq, db);
    if (intervalRef.current !== null) return;
    playPulse();
    intervalRef.current = window.setInterval(playPulse, (PULSE_ON + PULSE_OFF) * 1000);
  }, [playPulse, update]);

  const stop = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (offTimeoutRef.current !== null) {
      window.clearTimeout(offTimeoutRef.current);
      offTimeoutRef.current = null;
    }
    paramsRef.current = null;
    setIsOn(false);
  }, []);

  return { start, update, stop, isOn };
}
